import { motion } from "framer-motion";
import { Star, ChevronDown } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Service, Testimonial, Stat } from "@/lib/index";
import { springPresets, hoverLift } from "@/lib/motion";

export function ServiceCard({ service }: { service: Service }) {
  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="h-full"
    >
      <Card className="h-full flex flex-col overflow-hidden border-border/50 shadow-[0_8px_30px_-6px_color-mix(in_srgb,var(--primary)_15%,transparent)] hover:shadow-[0_12px_40px_-8px_color-mix(in_srgb,var(--primary)_25%,transparent)] transition-shadow duration-300">
        <div className="relative h-48 overflow-hidden">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/50 to-transparent" />
          <div className="absolute bottom-4 left-4">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 backdrop-blur-sm flex items-center justify-center text-2xl">
              {service.icon}
            </div>
          </div>
        </div>
        
        <CardHeader>
          <CardTitle className="text-2xl">{service.title}</CardTitle>
          <CardDescription className="text-base">{service.description}</CardDescription>
        </CardHeader>
        
        <CardContent className="flex-1 space-y-4">
          <div>
            <h4 className="font-semibold text-sm text-muted-foreground mb-2">Key Features</h4>
            <ul className="space-y-2">
              {service.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-sm text-muted-foreground mb-2 flex items-center gap-2">
              <Badge variant="secondary" className="bg-accent/10 text-accent border-accent/20">AI-Powered</Badge>
            </h4>
            <ul className="space-y-2">
              {service.aiCapabilities.map((capability, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <span className="text-accent mt-0.5">⚡</span>
                  <span>{capability}</span>
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
        
        <CardFooter>
          <Button className="w-full" size="lg">
            Learn More
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

export function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={springPresets.gentle}
    >
      <Card className="h-full border-border/50 shadow-[0_8px_30px_-6px_color-mix(in_srgb,var(--primary)_15%,transparent)]">
        <CardHeader>
          <div className="flex items-start gap-4">
            <Avatar className="w-16 h-16 border-2 border-primary/20">
              <AvatarImage src={testimonial.image} alt={testimonial.name} />
              <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                {testimonial.name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <CardTitle className="text-lg">{testimonial.name}</CardTitle>
              <CardDescription className="text-sm">
                {testimonial.role} at {testimonial.company}
              </CardDescription>
              <div className="flex gap-1 mt-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      i < testimonial.rating
                        ? 'fill-primary text-primary'
                        : 'fill-muted text-muted'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground italic leading-relaxed">
            "{testimonial.content}"
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function StatCard({ stat }: { stat: Stat }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={springPresets.snappy}
    >
      <Card className="text-center border-border/50 shadow-[0_8px_30px_-6px_color-mix(in_srgb,var(--primary)_15%,transparent)] hover:shadow-[0_12px_40px_-8px_color-mix(in_srgb,var(--primary)_25%,transparent)] transition-all duration-300">
        <CardHeader className="pb-2">
          <div className="w-12 h-12 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center text-2xl mb-4">
            {stat.icon}
          </div>
          <CardTitle className="text-5xl font-bold bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">
            {stat.value}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="font-semibold text-lg mb-1">{stat.label}</p>
          <p className="text-sm text-muted-foreground">{stat.description}</p>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function FAQItem({ question, answer }: { question: string; answer: string }) {
  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="item-1" className="border-border/50">
        <AccordionTrigger className="text-left hover:text-primary transition-colors">
          <span className="font-semibold">{question}</span>
        </AccordionTrigger>
        <AccordionContent className="text-muted-foreground leading-relaxed">
          {answer}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}